import { useEffect, useRef, useState, type MutableRefObject } from "react";

import { useInChatSearch } from "~/features/in-chat-search";

// In-chat search активного чата: режим/значение поиска, click-outside-закрытие
// и проксирование выбора результата в VirtualMessageList через scrollToMessageRef
// (он умеет авто-разворачивать свёрнутые date-группы перед scroll'ом).
export function useChatHeaderSearch(
    chatId: string | null | undefined,
    scrollToMessageRef?: MutableRefObject<((messageId: string) => void) | null>,
) {
    const [searchMode, setSearchMode] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const searchContainerRef = useRef<HTMLDivElement | null>(null);

    const openSearch = () => setSearchMode(true);
    const closeSearch = () => {
        setSearchMode(false);
        setSearchValue("");
    };

    const inChatSearchQuery = useInChatSearch(searchValue, searchMode ? (chatId ?? null) : null);
    const searchMessages = inChatSearchQuery.data?.messages ?? [];

    // Закрываем поиск и проксируем скролл. rAF перед вызовом — даём header'у
    // выйти из search-mode (вернётся обычный layout), чтобы scrollIntoView
    // работал по финальной геометрии.
    const handleResultSelect = (messageId: string) => {
        closeSearch();
        const scrollToMessage = scrollToMessageRef?.current;
        if (!scrollToMessage) return;
        requestAnimationFrame(() => {
            scrollToMessage(messageId);
        });
    };

    // Click-outside для закрытия. Esc обрабатывается на input через keydown.
    useEffect(() => {
        if (!searchMode) return;
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (searchContainerRef.current?.contains(target)) return;
            closeSearch();
        };
        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [searchMode]);

    return {
        searchMode,
        searchValue,
        setSearchValue,
        openSearch,
        closeSearch,
        searchContainerRef,
        searchMessages,
        isFetching: inChatSearchQuery.isFetching,
        handleResultSelect,
    };
}
